<template>
  <div class="dynamic-page-wrapper">
    <div v-if="loading" class="page-loading">
      <div class="loader-spinner"></div>
      <span>加载中...</span>
    </div>
    <div v-else-if="error" class="page-error">
      <p>{{ error }}</p>
    </div>
    <div
      v-else
      ref="contentEl"
      class="page-content"
      v-html="bodyHtml"
    ></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, computed, nextTick } from 'vue'
import { useRoute } from 'vue-router'

console.log('[DynamicPageView] ✅ 组件已加载')

const route = useRoute()

const pagePath = computed(() => {
  if (route.params.pagePath) return route.params.pagePath
  return route.path.replace(/^\//, '')
})

const bodyHtml = ref('')
const loading = ref(true)
const error = ref('')
const contentEl = ref(null)
let observer = null

const ANIMATION_CLASSES = ['animate-fade-in', 'animate-fade-left', 'animate-fade-right', 'animate-scale-in']
const ANIMATION_SELECTOR = ANIMATION_CLASSES.map(c => '.' + c).join(',')
const SKIP_TAGS = { script: 1, style: 1, link: 1, meta: 1, noscript: 1, title: 1, base: 1 }

// 提取后端返回 HTML 中的 <link>、编辑器CSS 和 <body> 内容
function extractBodyContent(html) {
  // 1. 提取 <link> 标签（如 Font Awesome 图标库）
  const linkRegex = /<link[^>]*rel=["']stylesheet["'][^>]*>/gi
  let links = ''
  let match
  while ((match = linkRegex.exec(html)) !== null) {
    links += match[0] + '\n'
  }

  // 2. 只提取编辑器CSS（id="editor-css"），不提取后端的基座样式
  let editorStyles = ''
  const editorStyleRegex = /<style\s+id=["']editor-css["'][^>]*>([\s\S]*?)<\/style>/i
  const styleMatch = html.match(editorStyleRegex)
  if (styleMatch) {
    editorStyles = styleMatch[1] + '\n'
  }

// 3. 提取 <body> 内容
  const bodyRegex = /<body[^>]*>([\s\S]*?)<\/body>/i
  const bodyMatch = html.match(bodyRegex)
  let body = bodyMatch ? bodyMatch[1] : html

  // 4. 移除 script 标签（后端 script 是为 iframe 设计的，v-html 中不需要）
  body = body.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')

  return `${links}<style>${editorStyles}
</style>${body}`
}

// 给容器直接子元素注入动画类名
function injectAnimationClasses(root) {
  const children = root.children
  let count = 0
  for (let i = 0; i < children.length; i++) {
    const el = children[i]
    if (SKIP_TAGS[el.tagName.toLowerCase()]) continue
    if (ANIMATION_CLASSES.some(c => el.classList.contains(c))) continue
    el.classList.add('animate-fade-in')
    if (count < 5) {
      el.classList.add('animate-delay-' + (count + 1))
    }
    count++
  }
}

// 启动滚动动画
function setupScrollAnimation() {
  const root = contentEl.value
  if (!root) {
    console.log('[动画] contentEl 不存在，跳过')
    return
  }

  // 1. 注入动画类名
  injectAnimationClasses(root)

  // 2. 查找所有动画元素
  const animatedEls = root.querySelectorAll(ANIMATION_SELECTOR)
  console.log('[动画] 页面元素总数:', root.querySelectorAll('*').length)
  console.log('[动画] 动画元素数:', animatedEls.length)

  if (animatedEls.length === 0) {
    console.log('[动画] 无动画元素')
    root.classList.add('anim-ready')
    return
  }

  // 3. 首屏可见元素直接显示
  let visibleCount = 0
  animatedEls.forEach(el => {
    const rect = el.getBoundingClientRect()
    if (rect.top < window.innerHeight - 60 && rect.height > 0) {
      el.classList.add('visible')
      visibleCount++
    }
  })
  console.log('[动画] 首屏可见:', visibleCount, '/', animatedEls.length)

  // 4. 激活动画模式：没有 .visible 的元素进入"待动画"隐藏状态
  root.classList.add('anim-ready')

  // 5. IntersectionObserver 监听滚动
  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible')
        observer.unobserve(entry.target)
      }
    })
  }, { threshold: 0.1 })

  const invisibleEls = root.querySelectorAll(
    ANIMATION_CLASSES.map(c => '.' + c + ':not(.visible)').join(',')
  )
  console.log('[动画] 等待滚动触发:', invisibleEls.length)
  invisibleEls.forEach(el => observer.observe(el))
}

/**
 * 手机端布局增强
 * 背景：编辑器保存时可能把元素的内联 style 转成 #ipXX 自动 ID 的 CSS 规则，
 * 导致本组件原来的 [style*="display:flex"] 等属性选择器失效，移动端不再堆叠。
 * 这里依据「计算样式」强制 grid/flex 容器堆叠，同时保留轮播、页脚三列等特殊布局。
 */
function enhanceMobileLayout() {
  const root = contentEl.value
  if (!root) return
  if (window.innerWidth > 768) return

  const preserveClasses = ['c-wrapper', 'c-row', 'c-track', 'c-slide', 'c-dots', 'c-arrow']
  const preserveIdHints = ['ilu9my']   // 页脚三列等需要保持 grid/flex 的容器

  const els = root.querySelectorAll('*')
  for (let i = 0; i < els.length; i++) {
    const el = els[i]
    if (el.closest('.c-wrapper')) continue
    if (preserveClasses.some(c => el.classList.contains(c))) continue
    const id = el.id || ''
    if (preserveIdHints.some(h => id.includes(h))) continue
    const disp = window.getComputedStyle(el).display
    const style = el.style
    if (disp === 'grid' || disp === 'inline-grid') {
      style.setProperty('display', 'block', 'important')
      style.setProperty('grid-template-columns', 'none', 'important')
    } else if (disp === 'flex' || disp === 'inline-flex') {
      style.setProperty('display', 'block', 'important')
      style.setProperty('flex-direction', 'column', 'important')
      style.setProperty('flex-wrap', 'wrap', 'important')
    }
  }
}

/**
 * 手机端轮播激活
 * 查找页面中已由数据库 HTML 提供好的 .c-wrapper 结构，
 * 为其添加交互行为（箭头切换、指示点、3s 自动播放）
 */
function enhanceMobileGridCarousels() {
  const root = contentEl.value
  if (!root) return

  const wrappers = root.querySelectorAll('.c-wrapper')
  if (!wrappers.length) {
    console.log('[轮播] 页面中无 .c-wrapper 结构，跳过')
    return
  }

  console.log('[轮播] 找到', wrappers.length, '个轮播容器')
  let activatedCount = 0

  wrappers.forEach(wrapper => {
    const track = wrapper.querySelector('.c-track')
    const slides = wrapper.querySelectorAll('.c-slide')
    const dots = wrapper.querySelectorAll('.c-dots span')
    const prevBtn = wrapper.querySelector('.c-arrow-prev')
    const nextBtn = wrapper.querySelector('.c-arrow-next')
    if (!track || !slides.length || !prevBtn || !nextBtn) return

    const total = slides.length
    if (total <= 1) return

    let currentIndex = 0
    let timer = null

    const updateSlide = () => {
      track.style.transform = `translateX(-${currentIndex * 100}%)`
      dots.forEach((d, i) => d.classList.toggle('active', i === currentIndex))
    }

    const startPlay = () => {
      if (timer) clearInterval(timer)
      timer = setInterval(() => {
        currentIndex = (currentIndex + 1) % total
        updateSlide()
      }, 3000)
    }

    const stopPlay = () => {
      if (timer) { clearInterval(timer); timer = null }
    }

    prevBtn.addEventListener('click', () => {
      currentIndex = (currentIndex - 1 + total) % total
      updateSlide(); stopPlay(); startPlay()
    })
    nextBtn.addEventListener('click', () => {
      currentIndex = (currentIndex + 1) % total
      updateSlide(); stopPlay(); startPlay()
    })
    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        currentIndex = i
        updateSlide(); stopPlay(); startPlay()
      })
    })
    wrapper.addEventListener('mouseenter', stopPlay)
    wrapper.addEventListener('mouseleave', startPlay)

    activatedCount++
    if (window.innerWidth <= 768) startPlay()
  })

  console.log('[轮播] 激活', activatedCount, '个轮播')
}

async function fetchPage(path) {
  loading.value = true
  error.value = ''
  bodyHtml.value = ''
  if (observer) {
    observer.disconnect()
    observer = null
  }
  try {
    const res = await fetch(`/api/editor/site/pages/${path}/view`)
    if (!res.ok) {
      error.value = '页面不存在或尚未发布'
      loading.value = false
      console.log('[DynamicPageView] API 返回错误:', res.status)
      return
    }

    const fullHtml = await res.text()
    console.log('[DynamicPageView] API 返回成功，HTML长度:', fullHtml.length)
    bodyHtml.value = extractBodyContent(fullHtml)
    loading.value = false
    await nextTick()
    console.log('[DynamicPageView] nextTick 完成，启动动画')
    setupScrollAnimation()
    // 手机端：强制 grid/flex 堆叠（兼容编辑器把内联样式转成 #ipXX 规则的情况）
    enhanceMobileLayout()
    // 手机端：将多个卡片的网格区域增强为轮播（左右箭头 + 3s 自动播放）
    enhanceMobileGridCarousels()
  } catch (e) {
    console.error('[DynamicPageView] 加载失败:', e)
    error.value = '无法加载页面：' + e.message
    loading.value = false
  }
}

const onWindowResize = () => {
  enhanceMobileLayout()
}

onMounted(() => {
  console.log('[DynamicPageView] onMounted, pagePath:', pagePath.value)
  window.addEventListener('resize', onWindowResize)
  fetchPage(pagePath.value)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onWindowResize)
  if (observer) {
    observer.disconnect()
    observer = null
  }
})

watch(pagePath, (newPath) => {
  if (newPath) {
    console.log('[DynamicPageView] 路由变化:', newPath)
    fetchPage(newPath)
  }
})
</script>

<style scoped>
.dynamic-page-wrapper {
  width: 100%;
  min-height: 100vh;
  position: relative;
}

.page-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 20px;
  color: #666;
  font-size: 15px;
}

.loader-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid #e0e0e0;
  border-top-color: #4a90d9;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.page-error {
  text-align: center;
  padding: 80px 20px;
  color: #999;
  font-size: 15px;
}

.page-content {
  width: 100%;
  min-height: 100vh;
  background: #fff;
}

/* 隔离编辑器页面的基础样式 */
.page-content :deep(img) {
  height: auto;
}

</style>

<!-- 滚动动画样式（非 scoped，作用于 v-html 注入的元素） -->
<!-- 策略：默认元素可见（防白屏），JS 初始化后给 .page-content 加 .anim-ready，非 .visible 元素才进入隐藏+动画待命状态 -->
<style>
/* 链接继承父元素文字颜色 */
.page-content a {
  color: inherit !important;
}

/* 默认：元素可见 */
.animate-fade-in,
.animate-fade-left,
.animate-fade-right,
.animate-scale-in {
  opacity: 1;
}

/* anim-ready 激活后：非 visible 元素进入隐藏+待动画状态 */
.page-content.anim-ready .animate-fade-in:not(.visible),
.page-content.anim-ready .animate-fade-left:not(.visible),
.page-content.anim-ready .animate-fade-right:not(.visible),
.page-content.anim-ready .animate-scale-in:not(.visible) {
  opacity: 0;
  visibility: visible;
  transition: opacity 0.8s ease, transform 0.8s ease;
}

.page-content.anim-ready .animate-fade-in:not(.visible) { transform: translateY(40px); }
.page-content.anim-ready .animate-fade-left:not(.visible) { transform: translateX(-50px); }
.page-content.anim-ready .animate-fade-right:not(.visible) { transform: translateX(50px); }
.page-content.anim-ready .animate-scale-in:not(.visible) { transform: scale(0.9); }

/* visible 状态：显示 + 恢复 */
.page-content.anim-ready .animate-fade-in.visible,
.page-content.anim-ready .animate-fade-left.visible,
.page-content.anim-ready .animate-fade-right.visible,
.page-content.anim-ready .animate-scale-in.visible {
  opacity: 1;
  transform: translate(0) scale(1);
}

/* 延迟变体 */
.animate-delay-1.visible { transition-delay: 0.15s; }
.animate-delay-2.visible { transition-delay: 0.30s; }
.animate-delay-3.visible { transition-delay: 0.45s; }
.animate-delay-4.visible { transition-delay: 0.60s; }
.animate-delay-5.visible { transition-delay: 0.75s; }

@media (prefers-reduced-motion: reduce) {
  .page-content.anim-ready .animate-fade-in:not(.visible),
  .page-content.anim-ready .animate-fade-left:not(.visible),
  .page-content.anim-ready .animate-fade-right:not(.visible),
  .page-content.anim-ready .animate-scale-in:not(.visible) {
    opacity: 1;
    transform: none;
    transition: none;
  }
}

/* 手机端：简介图片在其容器中垂直居中（针对编辑器生成的 ID 选择器规则） */
@media (max-width: 768px) {
  .page-content [id*="iv0by5"] {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
}

/* ===== 手机端轮播样式 ===== */
/* mc-desktop-grid 是数据库 HTML 中的标记类，手机端隐藏它，转用 c-wrapper 轮播 */
@media (max-width: 768px) {
  .mc-desktop-grid { display: none !important; }
}
@media (min-width: 769px) {
  .c-wrapper { display: none !important; }
}

/* 轮播基础 — 全局生效（不用 !important，让数据库 CSS 里带 !important 的覆盖这些） */
.c-wrapper { width: 100%; margin-top: 24px; }
.c-row {
  display: flex;
  align-items: center;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 0;
  width: 100%;
}
.c-arrow {
  flex: 0 0 auto;
  width: 36px; height: 36px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid #d9d9d9;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer; z-index: 2;
  font-size: 14px; color: #333;
  transition: all 0.25s ease;
  padding: 0; margin: 0;
  user-select: none;
  box-sizing: border-box;
  line-height: 1;
  box-shadow: 0 2px 6px rgba(0,0,0,0.12);
}
.c-arrow:hover { background: #1890ff; border-color: #1890ff; color: #fff; }
.c-arrow:active { transform: scale(0.92); }
.c-view {
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  border-radius: 8px;
}
.c-track {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  will-change: transform;
}
.c-slide {
  min-width: 100%; max-width: 100%;
  flex-shrink: 0; box-sizing: border-box;
}
.c-dots {
  display: flex;
  justify-content: center;
  gap: 8px; margin-top: 12px;
}
.c-dots span {
  width: 8px; height: 8px; border-radius: 50%;
  background: #d9d9d9; cursor: pointer;
  transition: all 0.3s; display: inline-block;
}
.c-dots span.active {
  background: #1890ff;
  width: 20px; border-radius: 4px;
}
@media (max-width: 480px) {
  .c-arrow { width: 30px; height: 30px; font-size: 13px; }
}

/* ===== 移动端适配：强制编辑器内容自适应换行 + 固定高度可滚动 ===== */
@media (max-width: 768px) {
  /* --- 固定高度 + 允许纵向滚动（禁止横向）--- */
  .dynamic-page-wrapper {
    overflow: hidden auto !important;
    height: calc(100vh - var(--header-height, 70px)) !important;
    width: 100% !important;
    max-width: 100vw !important;
  }
  #app .page-content {
    width: 100% !important;
    max-width: 100% !important;
  }

  /* ---- 图片、视频、iframe 等比缩放 ---- */
  #app .page-content img,
  #app .page-content video,
  #app .page-content iframe,
  #app .page-content canvas,
  #app .page-content svg {
    max-width: 100% !important;
    height: auto !important;
  }

  /* ---- 强制所有块级元素的宽度 ≤ 屏幕 ---- */
  #app .page-content div,
  #app .page-content section,
  #app .page-content article,
  #app .page-content main,
  #app .page-content header,
  #app .page-content footer,
  #app .page-content aside,
  #app .page-content nav,
  #app .page-content figure,
  #app .page-content [class*="col-"],
  #app .page-content [class*="column"],
  #app .page-content [class*="row"],
  #app .page-content [class*="grid"],
  #app .page-content [class*="container"] {
    max-width: 100% !important;
    box-sizing: border-box !important;
  }

  /* ---- 强制 Flex 容器换行 ---- */
  #app .page-content [style*="display: flex"],
  #app .page-content [style*="display:flex"] {
    flex-direction: column !important;
    flex-wrap: wrap !important;
    align-items: stretch !important;
  }

  /* ---- 强制 Grid 变成单列 ---- */
  #app .page-content [style*="display: grid"],
  #app .page-content [style*="display:grid"] {
    grid-template-columns: 1fr !important;
    display: block !important;
  }

  /* ---- 覆盖 GrapesJS 列/容器的固定宽度 ---- */
  #app .page-content div[style*="width:"],
  #app .page-content section[style*="width:"],
  #app .page-content article[style*="width:"],
  #app .page-content figure[style*="width:"],
  #app .page-content [class*="col-"],
  #app .page-content [class*="column"],
  #app .page-content [class*="row"] {
    max-width: 100% !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
  /* 覆盖 Flex 子项固定比例 */
  #app .page-content [style*="flex:"] {
    flex: 1 1 100% !important;
    max-width: 100% !important;
  }
  #app .page-content [style*="flex-basis"] {
    flex-basis: 100% !important;
    max-width: 100% !important;
  }

  /* ---- 文字自动换行 ---- */
  #app .page-content p,
  #app .page-content h1, #app .page-content h2, #app .page-content h3,
  #app .page-content h4, #app .page-content h5, #app .page-content h6,
  #app .page-content li, #app .page-content a, #app .page-content span,
  #app .page-content td, #app .page-content th {
    word-break: break-word !important;
    overflow-wrap: break-word !important;
    max-width: 100% !important;
  }

  /* ---- 表格横向滚动 ---- */
  #app .page-content table {
    display: block !important;
    overflow-x: auto !important;
    max-width: 100% !important;
    width: 100% !important;
  }
  #app .page-content td,
  #app .page-content th {
    white-space: normal !important;
  }

  /* ---- 页脚快速链接/服务支持/联系我们 三列并排（覆盖 grid→block 强制） ---- */
  #app .page-content [id*="ilu9my"] {
    display: grid !important;
    grid-template-columns: repeat(3, 1fr) !important;
    gap: 12px !important;
  }
  /* 第一个子元素（logo/名称）占满一行 */
  #app .page-content [id*="ilu9my"] > *:first-child {
    grid-column: 1 / -1;
  }
  /* 小屏手机：三列变两列 */
  @media (max-width: 420px) {
    #app .page-content [id*="ilu9my"] {
      grid-template-columns: repeat(2, 1fr) !important;
      gap: 8px !important;
    }
    #app .page-content [id*="ilu9my"] > *:first-child {
      grid-column: 1 / -1;
    }
  }

  /* ---- 轮播 flex 行布局保护（防止被 Flex→column 和 Grid→block 覆盖）---- */
  #app .page-content .c-row {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    align-items: center !important;
  }
  #app .page-content .c-track {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
  }
}
</style>
